/**
 * Product Photo Capture Component
 * ================================
 * Full-screen modal webcam-based photo capture for AI product identification.
 * Opens as a modal overlay similar to barcode scanner for better UX.
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { Camera, X, RefreshCw, Sparkles, AlertCircle, Settings, ExternalLink } from 'lucide-react'
import Button from './Button'

interface ProductPhotoCaptureProps {
  onCapture: (imageBase64: string) => void
  isAnalyzing?: boolean
}

type CaptureState = 'idle' | 'camera' | 'preview'
type ErrorType = 'no_camera' | 'permission_denied' | 'unknown' | null

export default function ProductPhotoCapture({ onCapture, isAnalyzing = false }: ProductPhotoCaptureProps) {
  const [showModal, setShowModal] = useState(false)
  const [state, setState] = useState<CaptureState>('idle')
  const [error, setError] = useState<ErrorType>(null)
  const [capturedImage, setCapturedImage] = useState<string | null>(null)

  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  // Start camera
  const startCamera = useCallback(async () => {
    setError(null)
    setState('camera')

    try {
      // Check for camera support
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('no_camera')
        setState('idle')
        return
      }

      // Try to get rear camera first (mobile), fallback to any camera
      let stream: MediaStream
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920, min: 1280 },
            height: { ideal: 1080, min: 720 },
          },
          audio: false,
        })
      } catch {
        // Fallback to any camera
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        })
      }

      streamRef.current = stream

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
      }
    } catch (err) {
      console.error('[ProductPhotoCapture] Camera error:', err)
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setError('permission_denied')
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          setError('no_camera')
        } else {
          setError('unknown')
        }
      } else {
        setError('unknown')
      }
      setState('idle')
    }
  }, [])

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
  }, [])

  // Capture photo
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return

    const video = videoRef.current
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')

    if (!ctx) return

    // Set canvas size to video dimensions
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    // Draw video frame to canvas
    ctx.drawImage(video, 0, 0)

    // Get base64 image
    const imageData = canvas.toDataURL('image/jpeg', 0.9)
    setCapturedImage(imageData)

    // Stop camera and show preview
    stopCamera()
    setState('preview')
  }, [stopCamera])

  // Retake photo
  const retakePhoto = useCallback(() => {
    setCapturedImage(null)
    startCamera()
  }, [startCamera])

  // Open modal and start camera
  const openModal = useCallback(() => {
    setShowModal(true)
    setCapturedImage(null)
    setState('idle')
    setError(null)
    // Start camera after a short delay to ensure modal is rendered
    setTimeout(() => {
      startCamera()
    }, 100)
  }, [startCamera])

  // Close modal
  const closeModal = useCallback(() => {
    stopCamera()
    setCapturedImage(null)
    setState('idle')
    setError(null)
    setShowModal(false)
  }, [stopCamera])

  // Analyze captured image
  const analyzeImage = useCallback(() => {
    if (capturedImage) {
      onCapture(capturedImage)
      closeModal()
    }
  }, [capturedImage, onCapture, closeModal])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera()
    }
  }, [stopCamera])

  // Render error content
  const renderError = () => {
    if (!error) return null

    const errorConfig = {
      no_camera: {
        title: 'No Camera Found',
        message: "We couldn't find a camera on your device. Make sure your camera is connected and try again.",
        showPermissionHelp: false,
      },
      permission_denied: {
        title: 'Camera Access Denied',
        message: "Please allow camera permissions to use AI product identification.",
        showPermissionHelp: true,
      },
      unknown: {
        title: 'Camera Error',
        message: 'Something went wrong while accessing your camera. Please try again.',
        showPermissionHelp: false,
      },
    }

    const { title, message, showPermissionHelp } = errorConfig[error]

    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <AlertCircle className="text-red-500" size={48} />
        <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 max-w-sm">{message}</p>

        {showPermissionHelp && (
          <div className="mt-4 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg text-left w-full max-w-sm">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
              <Settings size={16} />
              How to enable camera access:
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 font-medium">Browser settings:</p>
            <ol className="text-xs text-gray-600 dark:text-gray-400 space-y-1 ml-6 list-decimal mb-3">
              <li>Click the <span className="inline-flex items-center gap-0.5 font-medium">lock icon <ExternalLink size={10} /></span> in your browser&apos;s address bar</li>
              <li>Find <span className="font-medium">&quot;Camera&quot;</span> and set to <span className="font-medium">&quot;Allow&quot;</span></li>
            </ol>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 font-medium">Windows 10/11 users:</p>
            <ol className="text-xs text-gray-600 dark:text-gray-400 space-y-1 ml-6 list-decimal">
              <li>Open <span className="font-medium">Settings → Privacy → Camera</span></li>
              <li>Enable <span className="font-medium">&quot;Allow desktop apps to access your camera&quot;</span></li>
            </ol>
          </div>
        )}

        <div className="mt-6 flex gap-3">
          {showPermissionHelp && (
            <button
              onClick={() => {
                setError(null)
                startCamera()
              }}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
            >
              <RefreshCw size={16} />
              Try Again
            </button>
          )}
          <button
            onClick={closeModal}
            className={`px-4 py-2 rounded-lg transition-colors ${
              showPermissionHelp
                ? 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                : 'bg-primary-600 text-white hover:bg-primary-700'
            }`}
          >
            {showPermissionHelp ? 'Cancel' : 'Got it'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Compact card with start button */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-primary-600 dark:text-primary-400" />
            <h3 className="font-medium text-gray-900 dark:text-white">AI Product Identification</h3>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Take a photo of your product and let AI identify it
          </p>
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="flex flex-col items-center justify-center py-4">
            <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mb-3">
              <Camera size={28} className="text-primary-600 dark:text-primary-400" />
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-center mb-4 text-sm max-w-xs">
              Point your camera at a product and AI will identify it, search for details, and find images.
            </p>
            <Button onClick={openModal} disabled={isAnalyzing}>
              {isAnalyzing ? (
                <>
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Camera size={16} className="mr-2" />
                  Start Camera
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Full-screen Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          {/* Hidden canvas for capture */}
          <canvas ref={canvasRef} className="hidden" />

          {/* Close button */}
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
          >
            <X size={24} />
          </button>

          {/* Main content */}
          <div className="relative w-full max-w-2xl mx-4">
            {error ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden">
                {renderError()}
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b dark:border-gray-700 flex items-center gap-3">
                  <Sparkles className="text-primary-600" size={24} />
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {state === 'preview' ? 'Photo Captured!' : 'AI Product Identification'}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {state === 'preview'
                        ? 'Review the photo and click Identify to analyze'
                        : 'Position the product in the frame and take a photo'}
                    </p>
                  </div>
                </div>

                {/* Video/Preview container */}
                <div className="relative aspect-[4/3] bg-black">
                  {/* Camera view */}
                  {state === 'camera' && (
                    <>
                      <video
                        ref={videoRef}
                        className="w-full h-full object-cover"
                        autoPlay
                        playsInline
                        muted
                      />

                      {/* Guide overlay */}
                      <div className="absolute inset-0 pointer-events-none">
                        {/* Semi-transparent overlay with center cutout */}
                        <div className="absolute inset-0 bg-black/30" />

                        {/* Center focus area */}
                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[70%]">
                          {/* Clear area */}
                          <div className="absolute inset-0" style={{
                            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.3)'
                          }} />

                          {/* Corner brackets */}
                          <div className="absolute inset-0">
                            {/* Top-left */}
                            <div className="absolute top-0 left-0 w-12 h-12 border-l-4 border-t-4 border-primary-500" />
                            {/* Top-right */}
                            <div className="absolute top-0 right-0 w-12 h-12 border-r-4 border-t-4 border-primary-500" />
                            {/* Bottom-left */}
                            <div className="absolute bottom-0 left-0 w-12 h-12 border-l-4 border-b-4 border-primary-500" />
                            {/* Bottom-right */}
                            <div className="absolute bottom-0 right-0 w-12 h-12 border-r-4 border-b-4 border-primary-500" />
                          </div>
                        </div>
                      </div>

                      {/* Capture button overlay */}
                      <div className="absolute bottom-6 left-0 right-0 flex justify-center">
                        <button
                          onClick={capturePhoto}
                          className="w-20 h-20 rounded-full bg-white border-4 border-primary-500 flex items-center justify-center hover:bg-gray-100 transition-colors shadow-lg"
                          title="Take Photo"
                        >
                          <div className="w-14 h-14 rounded-full bg-primary-500" />
                        </button>
                      </div>
                    </>
                  )}

                  {/* Preview captured image */}
                  {state === 'preview' && capturedImage && (
                    <img
                      src={capturedImage}
                      alt="Captured product"
                      className="w-full h-full object-contain"
                    />
                  )}

                  {/* Loading state */}
                  {state === 'idle' && !error && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center text-white">
                        <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto" />
                        <p className="mt-4 text-sm">Initializing camera...</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer with actions */}
                {state === 'preview' && (
                  <div className="p-4 border-t dark:border-gray-700 flex gap-3 justify-center">
                    <button
                      onClick={retakePhoto}
                      className="flex items-center justify-center gap-2 px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <RefreshCw size={18} />
                      Retake
                    </button>
                    <button
                      onClick={analyzeImage}
                      className="flex items-center justify-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md"
                    >
                      <Sparkles size={18} />
                      Identify Product
                    </button>
                  </div>
                )}

                {/* Camera footer */}
                {state === 'camera' && (
                  <div className="p-4 border-t dark:border-gray-700 text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Position the product within the frame and tap the capture button
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
